import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import { useEffect, ReactElement } from "react";

import { RouteName } from "@/consts";
import { tipsTranslations } from "@/consts/pageTranslationNamespace";
import { TableTitle } from "@/features/common/components/Table/Layout";
import { TablePlaceholder } from "@/features/common/components/placeholder/TablePlaceholder";
import { View } from "@/features/common/components/tailwind/View";
import { useFeatureEnabled } from "@/features/common/hooks/useFeatureEnabled";
import { PaymentSettingLayout } from "@/features/section/components/paymentSetting/PaymentSettingLayout";
import { LoginRequired } from "@/features/user/components";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import { FeatureNameEnum } from "@/generated/generated-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";
import { trpc } from "@/helpers/trpc";

const TipsInvoiceTable = dynamic(
  () =>
    import("@/features/tips/components/tipsInvoiceTable/TipsInvoiceTable").then(
      (mod) => mod.TipsInvoiceTable,
    ),
  {
    ssr: false,
  },
);

const config = getPublicConfig();
export default function TipsInvoices() {
  const { t } = useTranslation(["tips", "common"]);
  const { user, ready } = useUserContext();
  const router = useRouter();

  useEffect(() => {
    if (ready && !user) {
      router.replace(RouteName.Home);
    }
  }, [ready, user]);

  if (!ready)
    return (
      <View className="flex flex-col">
        <TableTitle className="hidden md:block">
          {t(`common:pageNavigation:tipsRecord`)}
        </TableTitle>
        <TablePlaceholder />
      </View>
    );

  return (
    <>
      <Head>
        <title>{`${t("common:pageNavigation.tipsRecord")} | ${
          config.siteName
        }`}</title>
      </Head>
      <TableTitle className="hidden md:block">
        {t(`common:pageNavigation:tipsRecord`)}
      </TableTitle>

      {!user ? <LoginRequired /> : <TipsInvoiceTable />}
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, tipsTranslations)),
    },
  };
};

TipsInvoices.getLayout = function getLayout(
  page: ReactElement<{ isPayoutMethods: boolean }>,
) {
  return <PaymentSettingLayout>{page}</PaymentSettingLayout>;
};
